import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { SessionService } from '../../core/services/session.service';
import { AccountService, AccountPayload } from '../../core/services/account.service';
import { NgxTgModule, TranslateService } from '@worthy-ventures/metaglotta-ngx';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
    selector: 'app-account',
    imports: [CommonModule, FormsModule, MenuBarComponent, FooterComponent, AdminNavComponent, NgxTgModule],
    templateUrl: './account.component.html',
    styleUrl: './account.component.scss',
})
export class AccountComponent implements OnInit {
    loading = false;
    saving = false;

    username = '';
    email = '';
    name = '';
    mobile = '';
    password = '';
    passwordConfirm = '';

    emailInvalid = false;
    passwordMismatch = false;
    shake = false;

    /**
     * Cloud gateway customers have no local record: the legacy site's
     * user_details_gw.php gave them a password-only form posted to the cloud,
     * so that is all we show them here.
     */
    get isLocal(): boolean {
        return this.session.isLocal();
    }

    constructor(
        private accountService: AccountService,
        private session: SessionService,
        private router: Router,
        private toastr: ToastrService,
        private i18n: TranslateService,
    ) { }

    ngOnInit(): void {
        if (!this.session.isLoggedIn()) {
            this.router.navigate(['/']);
            return;
        }
        this.load();
    }

    load(): void {
        this.loading = true;
        this.accountService.get().subscribe({
            next: (res) => {
                const u = res?.user ?? {};
                this.username = u.username ?? '';
                this.email = u.email ?? '';
                this.name = u.name ?? '';
                this.mobile = u.mobile ?? '';
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.toastr.error(this.i18n.instant('account_toast_load_error'));
            },
        });
    }

    save(): void {
        // Cloud gateway customer: password is the only editable field.
        if (!this.isLocal) {
            this.savePasswordOnly();
            return;
        }

        this.emailInvalid = !EMAIL_RE.test(this.email.trim());
        this.passwordMismatch = !!this.password && this.password !== this.passwordConfirm;

        if (this.emailInvalid || this.passwordMismatch) {
            this.triggerShake();
            return;
        }

        const payload: AccountPayload = {
            email: this.email.trim(),
            name: this.name.trim(),
            mobile: this.mobile.trim(),
        };
        if (this.password) {
            payload.password = this.password;
            payload.passwordConfirm = this.passwordConfirm;
        }

        this.saving = true;
        this.accountService.update(payload).subscribe({
            next: () => {
                this.saving = false;
                this.password = '';
                this.passwordConfirm = '';
                this.toastr.success(this.i18n.instant('account_toast_updated'));
            },
            error: () => {
                this.saving = false;
                this.toastr.error(this.i18n.instant('account_toast_update_error'));
            },
        });
    }

    /** Password-only save (cloud gateway customers). */
    private savePasswordOnly(): void {
        this.emailInvalid = false;
        this.passwordMismatch = !this.password || this.password !== this.passwordConfirm;
        if (!this.password || this.passwordMismatch) {
            this.triggerShake();
            return;
        }

        this.saving = true;
        this.accountService.changePassword(this.password, this.passwordConfirm).subscribe({
            next: () => {
                this.saving = false;
                this.password = '';
                this.passwordConfirm = '';
                this.toastr.success(this.i18n.instant('account_toast_password_updated'));
            },
            error: (err) => {
                this.saving = false;
                this.toastr.error(err?.error?.message || this.i18n.instant('account_toast_password_error'));
            },
        });
    }

    private triggerShake(): void {
        this.shake = true;
        setTimeout(() => (this.shake = false), 500);
    }
}
